import { Button } from "@msuite/ui/button";
import { useDisclosure, useLoading } from "@msuite/ui/hooks";
import { Icon, type IconType, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import React from "react";
import { useNavigate } from "react-router";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { useProtocol } from "@/hooks/use-protocol";
import { SendProtocolModal } from "@/modals/protocols";
import type { ProtocolPanelBaseProps } from "./shared";

export function ProtocolMenu({ protocolId }: ProtocolPanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();
	const qc = useQueryClient();
	const asyncModal = useAsyncModal();
	const sendModal = useDisclosure();
	const printableLoader = useLoading();
	const { citadel } = useCitadel();

	/** Query */
	// The same query the page above already holds, so this costs a cache read: the rules for a
	// protocol condition on who wrote it and which project it is about, so the check needs the row.
	const { protocol } = useProtocol(protocolId);

	/** Functions */
	function handleOnNavigateToEdit() {
		navigate(`/protocols/${protocolId}/edit`);
	}

	async function handleOnDelete() {
		try {
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			const response = await client.protocols[":protocolId"].$delete({
				param: { protocolId },
			});
			await responseHandler(response);
			await qc.invalidateQueries({ predicate: invalidate(["protocols"]) });
			navigate("/protocols");
		} catch (error) {
			console.error(error);
		}
	}

	async function handleOnExportPdf() {
		try {
			printableLoader.start();
			const response = await client.protocols[":protocolId"].printable.$post({
				param: { protocolId },
			});
			await responseHandler(response);
			await qc.invalidateQueries({ predicate: invalidate([protocolId]) });
			toast.success(t("protocols.pdf_created"));
		} catch (error) {
			console.error(error);
		} finally {
			printableLoader.stop();
		}
	}

	/** Computed */
	// Sending leaves the house — the recipients include the client's own people — so it answers to
	// the same right as writing the minutes. Deleting is separate and narrower: a protocol is a
	// record, and only an administrator removes one.
	const resource = protocol
		? {
				id: protocol.id,
				orgId: protocol.orgId,
				createdBy: protocol.createdBy,
				projectId: protocol.projectId,
			}
		: null;
	const canUpdateProtocol = !!resource && citadel?.can("update", "Protocol", resource) === true;
	const canDeleteProtocol = !!resource && citadel?.can("delete", "Protocol", resource) === true;

	/** Render */
	return (
		<React.Fragment>
			<div className="h-stack">
				{canUpdateProtocol && (
					<Button
						icon={<Icon name="Send" />}
						onClick={sendModal.onOpen}
						data-cy="protocol-send-button"
					>
						{t("general.send")}
					</Button>
				)}
				{canUpdateProtocol && (
					<Button
						variant="primary"
						icon={<Icon name="Edit" />}
						onClick={handleOnNavigateToEdit}
						data-cy="protocol-edit-button"
					>
						{t("general.edit")}
					</Button>
				)}
				<Menu
					items={createStandardMenuItems({
						onDelete: canDeleteProtocol ? handleOnDelete : undefined,
						items: [
							{
								label: t("protocols.export_pdf"),
								icon: icon("FileText") as IconType,
								onClick: handleOnExportPdf,
								isDisabled: printableLoader.isLoading,
								cyId: "protocol-export-pdf-item",
							},
						],
					})}
				>
					<Button
						size="xs"
						icon={<Icon name="EllipsisVertical" />}
						variant="ghost"
						isLoading={printableLoader.isLoading}
						data-cy="protocol-menu-button"
					/>
				</Menu>
			</div>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("confirmation.confirm_deletion")}
				/>
			)}
			{sendModal.isOpen && (
				<SendProtocolModal
					protocolId={protocolId}
					isOpen={sendModal.isOpen}
					onClose={sendModal.onClose}
				/>
			)}
		</React.Fragment>
	);
}
